import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { getBundledCartSectionIds } from './cart-section-ids.js';
import { HdsCalculatorWalls } from '@theme/hds-calculator-walls';
import { HdsCalculatorOpenings } from '@theme/hds-calculator-openings';
import {
  buildCartSelectionPricing,
  buildPaintExplanationHtml,
  computeOpeningsAreaM2,
  calculatePaint,
  filterPurchasablePaintVariants,
  formatCartSelectionDisplay,
  formatMoneyFromCents,
  formatVolumeDisplay,
  metafieldValueToString,
  resolveCalculationMode,
  resolveConsumption,
  resolvePaintDataVariant,
  resolveRecommendedLayers,
  resolveVariantFinishLabel,
  resolveVariantLiters,
  variantHasPaintCoverageData,
  variantMatchesApplication,
} from '@theme/paint-calculator-core';

/** @type {RegExp} */
const VOLUME_OPTION_NAME_PATTERN = /inhoud|size|maat|grootte|afmeting|volume|liter|lengte/i;

/**
 * @param {string | null | undefined} optionName
 * @returns {boolean}
 */
function isVolumeOptionName(optionName) {
  return VOLUME_OPTION_NAME_PATTERN.test(optionName || '');
}

/**
 * Paint calculator on product pages.
 *
 * @extends {Component}
 */
class PaintCalculator extends Component {
  /** @type {HdsCalculatorWalls | null} */
  #walls = null;

  /** @type {HdsCalculatorOpenings | null} */
  #openings = null;

  /** @type {HTMLDialogElement | null} */
  #modal = null;

  /** @type {boolean} */
  #initialized = false;

  /** @type {Array<{id: number, title?: string | null, image_url?: string | null, coverage_text: string | null, consumption_value: number | string | null, consumption_unit: string | null, consumption_basis: string | null, recommended_layers: number | string | null, volume: number | null, volume_unit: string | null, m2_per_can: number | null, price: number | null, available: boolean | null, option1?: string | null, option2?: string | null, option3?: string | null}>} */
  #variants = [];

  /** @type {Array<string>} */
  #optionNames = [];

  /** @type {import('@theme/paint-calculator-core').OptimizedCombination | null} */
  #lastCombination = null;

  /** @type {import('@theme/paint-calculator-core').PaintCalculationResult | null} */
  #lastResult = null;

  /** @type {HTMLElement | null} */
  #triggerTemplate = null;

  /** @type {string | null} */
  #modalId = null;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {(event: Event) => void} */
  #onDelegatedTriggerClick = (event) => {
    const modalId = this.#modalId;
    if (!modalId || !(event.target instanceof Element)) return;

    const trigger = event.target.closest(`[data-hds-calculator-trigger][aria-controls="${modalId}"]`);
    if (!trigger) return;

    event.preventDefault();
    this.#openModal();
  };

  /** @type {(event: Event) => void} */
  #onDomReset = (event) => {
    const detail = /** @type {{ sectionId?: string, productId?: string } | undefined} */ (
      'detail' in event ? event.detail : undefined
    );

    if (
      detail?.sectionId &&
      this.dataset.sectionId &&
      String(detail.sectionId) !== String(this.dataset.sectionId)
    ) {
      return;
    }

    if (
      detail?.productId &&
      this.dataset.productId &&
      String(detail.productId) !== String(this.dataset.productId)
    ) {
      return;
    }

    this.#reloadVariantsFromDom();
    this.#scheduleLegendRelocation();
    if (this.#walls?.hasValidDimensions()) {
      this.#calculate();
    }
  };

  /** @type {(event: Event) => void} */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    const detail = event.detail;
    const eventProductId = detail?.data?.productId;

    if (detail?.data?.newProduct) {
      this.#reloadVariantsFromDom();
    } else if (
      eventProductId &&
      this.dataset.productId &&
      String(eventProductId) !== String(this.dataset.productId)
    ) {
      return;
    }

    const variantId = detail?.resource?.id;
    if (variantId) {
      this.#syncVariantDisplay(String(variantId));
      this.#syncRecommendedLayers(String(variantId));
      this.#calculate();
    }

    requestAnimationFrame(() => this.#relocateTriggerToVariantLegend());
  };

  updatedCallback() {
    super.updatedCallback();
    this.#reloadVariantsFromDom();
    const variantId = this.#getCurrentVariantId();
    this.#syncVariantDisplay(variantId);
    this.#syncRecommendedLayers(variantId);
    if (this.#walls?.hasValidDimensions()) {
      this.#calculate();
    } else {
      this.#hideResults();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#reloadVariantsFromDom();
    this.#modalId = this.querySelector('dialog')?.id ?? null;

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener('click', this.#onDelegatedTriggerClick);
    document.addEventListener('hds:main-morphed', this.#onDomReset);
    document.addEventListener('hds:loyalty-dom-reset', this.#onDomReset);
    document.addEventListener('hds:variant-picker-morphed', this.#onDomReset);
    document.addEventListener('shopify:section:load', this.#onDomReset);

    const trigger = this.querySelector('[data-hds-calculator-trigger]');
    if (trigger instanceof HTMLElement) {
      this.#triggerTemplate = /** @type {HTMLElement} */ (trigger.cloneNode(true));
    }

    this.querySelectorAll('[data-hds-calculator-close]').forEach((button) => {
      button.addEventListener('click', () => this.#closeModal());
    });

    this.querySelector('[data-hds-calculator-add-wall]')?.addEventListener('click', () => {
      this.#walls?.addWall();
    });

    this.querySelector('[data-hds-calculator-toggle-info]')?.addEventListener('click', () => {
      this.#toggleInformation();
    });

    this.querySelector('[data-hds-calculator-add-to-cart]')?.addEventListener('click', () => {
      this.#addToCart();
    });

    this.querySelector('[data-paint-layers-decrease]')?.addEventListener('click', () => {
      this.#changeLayers(-1);
    });

    this.querySelector('[data-paint-layers-increase]')?.addEventListener('click', () => {
      this.#changeLayers(1);
    });

    const layersInput = this.querySelector('[data-paint-layer-input]');
    layersInput?.addEventListener('change', () => this.#syncLayersFromInput());
    layersInput?.addEventListener('input', () => this.#syncLayersFromInput());

    this.querySelectorAll('[data-paint-reserve-percentage]').forEach((input) => {
      input.addEventListener('change', () => this.#calculate());
    });

    this.#syncLayerControls();
    this.#syncVariantDisplay(this.#initialVariantId());
    this.#syncRecommendedLayers(this.#initialVariantId());
    this.#scheduleLegendRelocation();
  }

  #scheduleLegendRelocation() {
    if (this.dataset.triggerPlacement !== 'variant_legend') return;

    const attempt = () => this.#relocateTriggerToVariantLegend();
    attempt();
    requestAnimationFrame(attempt);
    for (const delay of [0, 50, 150, 300, 600, 1200]) {
      window.setTimeout(attempt, delay);
    }
  }

  #getVariantPickerRoot() {
    return this.closest('.product-details, .product-information, main') ?? document;
  }

  #getVariantPicker() {
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;
    const root = this.#getVariantPickerRoot();
    const selector = sectionId
      ? `variant-picker[data-section-id="${sectionId}"]`
      : 'variant-picker';
    const candidates = [...root.querySelectorAll(selector)];

    if (productId) {
      const productMatch = candidates.find(
        (picker) => picker instanceof HTMLElement && picker.dataset.productId === String(productId)
      );
      if (productMatch instanceof HTMLElement) return productMatch;
    }

    if (candidates[0] instanceof HTMLElement) return candidates[0];

    const fallback = document.querySelector(selector);
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {Element} slot
   * @param {string} targetName
   * @returns {boolean}
   */
  #slotMatchesOptionName(slot, targetName) {
    const row = slot.closest('[data-option-name]');
    return row?.dataset.optionName?.trim().toLowerCase() === targetName;
  }

  #findLegendSlot() {
    const picker = this.#getVariantPicker();
    if (!picker) return null;

    const slots = [...picker.querySelectorAll('[data-variant-legend-actions]')];
    const targetName = (this.dataset.variantOptionName || '').trim().toLowerCase();

    if (targetName) {
      const exact = slots.find((slot) => this.#slotMatchesOptionName(slot, targetName));
      if (exact) return exact;
    }

    return (
      slots.find((slot) => {
        const row = slot.closest('[data-option-name]');
        return isVolumeOptionName(row?.dataset.optionName);
      }) || null
    );
  }

  #getTrigger() {
    const modal = this.querySelector('dialog');
    const modalId = modal?.id;

    if (modalId) {
      const externalTrigger = document.querySelector(
        `[data-hds-calculator-trigger][aria-controls="${modalId}"]`
      );
      if (externalTrigger instanceof HTMLElement && externalTrigger.isConnected) {
        return externalTrigger;
      }
    }

    const localTrigger = this.querySelector('[data-hds-calculator-trigger]');
    return localTrigger instanceof HTMLElement && localTrigger.isConnected ? localTrigger : null;
  }

  #getOrCreateTrigger() {
    const existing = this.#getTrigger();
    if (existing) return existing;

    if (!(this.#triggerTemplate instanceof HTMLElement)) return null;

    const trigger = /** @type {HTMLElement} */ (this.#triggerTemplate.cloneNode(true));
    this.insertBefore(trigger, this.firstChild);
    return trigger;
  }

  /**
   * @returns {boolean}
   */
  #isTriggerRelocated() {
    const trigger = this.#getTrigger();
    const slot = this.#findLegendSlot();
    return Boolean(trigger && slot && slot.contains(trigger));
  }

  #activateStandaloneFallback(trigger) {
    if (trigger.parentElement !== this) {
      this.insertBefore(trigger, this.firstChild);
    }

    trigger.classList.remove('hds-calculator__trigger--inline');
    delete this.dataset.relocated;
  }

  #relocateTriggerToVariantLegend() {
    if (this.dataset.triggerPlacement !== 'variant_legend') return;

    const slot = this.#findLegendSlot();
    const trigger = this.#getOrCreateTrigger();
    if (!trigger) return;

    if (slot) {
      if (!slot.contains(trigger)) {
        slot.appendChild(trigger);
      }
      trigger.classList.add('hds-calculator__trigger--inline');
      this.dataset.relocated = 'true';
      return;
    }

    if (this.#isTriggerRelocated()) {
      delete this.dataset.relocated;
    }

    this.#activateStandaloneFallback(trigger);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener('click', this.#onDelegatedTriggerClick);
    document.removeEventListener('hds:main-morphed', this.#onDomReset);
    document.removeEventListener('hds:loyalty-dom-reset', this.#onDomReset);
    document.removeEventListener('hds:variant-picker-morphed', this.#onDomReset);
    document.removeEventListener('shopify:section:load', this.#onDomReset);
  }

  #ensureInitialized() {
    if (this.#initialized) return;

    this.#modal = this.querySelector('dialog');
    if (!this.#modal) return;

    this.#modal.addEventListener('click', (event) => {
      if (event.target === this.#modal) this.#closeModal();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#modal?.open) this.#closeModal();
    });

    const wallsContainer = this.querySelector('[data-hds-calculator-walls]');
    if (wallsContainer instanceof HTMLElement) {
      this.#walls = new HdsCalculatorWalls({
        container: wallsContainer,
        onWallsChange: () => {
          this.#updateSectionBadges();
          this.#calculate();
        },
      });
      this.#walls.addInitialWall();
    }

    const openingsWrapper = this.querySelector('[data-hds-calculator-openings]');
    const openingsList = this.querySelector('[data-hds-calculator-openings-list]');
    const addOpeningButton = this.querySelector('[data-hds-calculator-add-opening]');
    if (openingsList instanceof HTMLElement) {
      this.#openings = new HdsCalculatorOpenings({
        container: openingsList,
        addButton: addOpeningButton instanceof HTMLElement ? addOpeningButton : null,
        onOpeningsChange: () => {
          this.#updateSectionBadges();
          this.#calculate();
        },
        labels: this.#getOpeningsLabels(openingsWrapper ?? openingsList),
      });
      this.#openings.addInitialOpening();
    }

    this.#initialized = true;
  }

  #openModal() {
    this.#ensureInitialized();
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    this.#modal?.showModal();
    this.#updateSectionBadges();
    this.#calculate();
  }

  #closeModal() {
    this.#modal?.close();
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, this.#previousScrollY);
  }

  #toggleInformation() {
    const information = this.querySelector('[data-hds-calculator-information]');
    const toggle = this.querySelector('[data-hds-calculator-toggle-info]');
    information?.classList.toggle('hidden');
    const isOpen = information && !information.classList.contains('hidden');
    if (toggle instanceof HTMLButtonElement) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      toggle.classList.toggle('is-open', Boolean(isOpen));
    }
  }

  /**
   * @returns {string | null}
   */
  #initialVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return String(fromUrl);
    if (this.dataset.initialVariantId) return String(this.dataset.initialVariantId);
    return null;
  }

  /**
   * @returns {Array<object>}
   */
  #parseVariants() {
    const script = this.querySelector('[data-paint-calculator-variants]');
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  #reloadVariantsFromDom() {
    this.#variants = this.#parseVariants();
    this.#optionNames = this.#parseOptionNames();
    this.#lastCombination = null;
    this.#lastResult = null;
  }

  /**
   * @param {string | null} variantId
   */
  #getVariantData(variantId) {
    if (!variantId) return null;
    return this.#variants.find((variant) => String(variant.id) === String(variantId)) ?? null;
  }

  /**
   * @returns {Array<string>}
   */
  #parseOptionNames() {
    const raw = this.dataset.productOptionNames;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((name) => String(name)) : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string | null} variantId
   */
  #syncVariantDisplay(variantId) {
    const variant = this.#getVariantData(variantId);
    const coverageEl = this.querySelector('[data-paint-coverage-display]');
    const finishEl = this.querySelector('[data-paint-finish-display]');

    if (!variant) {
      if (coverageEl) coverageEl.textContent = '—';
      if (finishEl) finishEl.textContent = '—';
      this.#syncTriggerVisibility(null);
      return;
    }

    if (finishEl) {
      finishEl.textContent = resolveVariantFinishLabel(variant, this.#optionNames) || '—';
    }

    const dataVariant = resolvePaintDataVariant(this.#variants, variant) ?? variant;
    const consumption = resolveConsumption(dataVariant, this.dataset.rangeStrategy || 'min');

    if (coverageEl) {
      if (consumption?.display) {
        coverageEl.textContent = consumption.display;
      } else if (dataVariant.m2_per_can) {
        coverageEl.textContent = `${dataVariant.m2_per_can} m²/blik`;
      } else {
        coverageEl.textContent = metafieldValueToString(dataVariant.coverage_text) || '—';
      }
    }

    this.#syncTriggerVisibility(variant);
  }

  /**
   * @param {object | null} variant
   */
  #syncTriggerVisibility(variant) {
    const trigger = this.#getTrigger();
    if (!(trigger instanceof HTMLElement)) return;

    const hasData = variantHasPaintCoverageData(variant, this.dataset.rangeStrategy || 'min');
    trigger.classList.toggle('hidden', !hasData);
  }

  /**
   * @param {string | null} variantId
   */
  #syncRecommendedLayers(variantId) {
    const variant = this.#getVariantData(variantId);
    const dataVariant = resolvePaintDataVariant(this.#variants, variant) ?? variant;
    const input = this.querySelector('[data-paint-layer-input]');
    if (!(input instanceof HTMLInputElement) || !dataVariant) return;

    const fallback = parseInt(this.dataset.defaultLayers || '2', 10) || 2;
    const recommended = resolveRecommendedLayers(dataVariant, fallback);
    input.value = String(this.#clampLayers(recommended));
    this.#syncLayerControls();
  }

  /**
   * @returns {number}
   */
  #getReservePercentage() {
    const selected = this.querySelector('[data-paint-reserve-percentage]:checked');
    if (!(selected instanceof HTMLInputElement)) return 0;

    const value = parseInt(selected.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * @returns {number}
   */
  #getMaxLayers() {
    const max = parseInt(this.dataset.maxLayers || '3', 10);
    return Number.isFinite(max) && max > 0 ? max : 3;
  }

  /**
   * @param {number} value
   * @returns {number}
   */
  #clampLayers(value) {
    return Math.min(this.#getMaxLayers(), Math.max(1, value));
  }

  /**
   * @param {number} delta
   */
  #changeLayers(delta) {
    const input = this.querySelector('[data-paint-layer-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const nextValue = this.#clampLayers((parseInt(input.value, 10) || 1) + delta);
    input.value = String(nextValue);
    this.#syncLayerControls();
    this.#calculate();
  }

  #syncLayersFromInput() {
    const input = this.querySelector('[data-paint-layer-input]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = String(this.#clampLayers(parseInt(input.value, 10) || 1));
    this.#syncLayerControls();
    this.#calculate();
  }

  #syncLayerControls() {
    const input = this.querySelector('[data-paint-layer-input]');
    const decreaseBtn = this.querySelector('[data-paint-layers-decrease]');
    const increaseBtn = this.querySelector('[data-paint-layers-increase]');
    if (!(input instanceof HTMLInputElement)) return;

    const value = this.#clampLayers(parseInt(input.value, 10) || 1);
    input.value = String(value);

    if (decreaseBtn instanceof HTMLButtonElement) {
      decreaseBtn.disabled = value <= 1;
    }
    if (increaseBtn instanceof HTMLButtonElement) {
      increaseBtn.disabled = value >= this.#getMaxLayers();
    }
  }

  /**
   * @returns {number}
   */
  #getSelectedLayers() {
    const input = this.querySelector('[data-paint-layer-input]');
    if (input instanceof HTMLInputElement) {
      const value = parseInt(input.value, 10);
      if (Number.isFinite(value) && value > 0) return this.#clampLayers(value);
    }
    return this.#clampLayers(parseInt(this.dataset.defaultLayers || '2', 10) || 2);
  }

  #updateSectionBadges() {
    const surfaceBadge = this.querySelector('[data-hds-calculator-surface-badge]');
    const openingsBadge = this.querySelector('[data-hds-calculator-openings-badge]');
    const reserveBadge = this.querySelector('[data-hds-calculator-reserve-badge]');
    const surfaceAreaM2 = this.#walls?.getTotalAreaM2() ?? 0;
    const openingsAreaM2 = computeOpeningsAreaM2(this.#openings?.getOpeningsForCalculation() ?? null);

    this.#setSurfaceBadge(surfaceBadge, surfaceAreaM2);
    this.#setOpeningsBadge(openingsBadge, openingsAreaM2);
    this.#setReserveBadge(reserveBadge);
  }

  /**
   * @param {HTMLElement} badge
   * @param {string} html
   */
  #setBadgeHtml(badge, html) {
    badge.innerHTML = html;
    badge.classList.remove('hidden');
  }

  /**
   * @param {HTMLElement} badge
   */
  #hideBadge(badge) {
    badge.textContent = '';
    badge.innerHTML = '';
    badge.classList.add('hidden');
  }

  /**
   * @param {number} areaM2
   * @param {number} layers
   * @returns {string}
   */
  #formatAreaBadgeHtml(areaM2, layers) {
    return `<span class="hds-calculator__section-badge-main">${this.#formatAreaM2(areaM2)} m²</span><span class="hds-calculator__section-badge-detail">(${this.#formatAreaM2(areaM2 * layers)} m² voor ${layers} lagen)</span>`;
  }

  /**
   * @param {Element | null} badge
   * @param {number} grossAreaM2
   */
  #setSurfaceBadge(badge, grossAreaM2) {
    if (!(badge instanceof HTMLElement)) return;

    if (grossAreaM2 <= 0) {
      this.#hideBadge(badge);
      return;
    }

    const layers = this.#getSelectedLayers();
    this.#setBadgeHtml(badge, this.#formatAreaBadgeHtml(grossAreaM2, layers));
  }

  /**
   * @param {Element | null} badge
   * @param {number} areaM2
   */
  #setOpeningsBadge(badge, areaM2) {
    if (!(badge instanceof HTMLElement)) return;

    if (areaM2 <= 0) {
      this.#hideBadge(badge);
      return;
    }

    const layers = this.#getSelectedLayers();
    this.#setBadgeHtml(badge, this.#formatAreaBadgeHtml(areaM2, layers));
  }

  /**
   * @param {Element | null} badge
   */
  #setReserveBadge(badge) {
    if (!(badge instanceof HTMLElement)) return;

    const result = this.#lastResult;
    if (result?.reservePercentage > 0 && result.reserveAreaM2 > 0) {
      badge.textContent = `+${this.#formatAreaM2(result.reserveAreaM2)} m²`;
      badge.classList.remove('hidden');
      return;
    }

    this.#hideBadge(badge);
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  #formatAreaM2(value) {
    return value.toFixed(2).replace('.', ',');
  }

  #calculate() {
    if (!this.#walls?.hasValidDimensions()) {
      this.#hideResults();
      return;
    }

    const variant = this.#getVariantData(this.#getCurrentVariantId());
    if (!variant) {
      this.#hideResults();
      return;
    }

    const dataVariant = resolvePaintDataVariant(this.#variants, variant) ?? variant;
    const activeApplication = this.#getActiveApplication();
    let optimizableVariants = filterPurchasablePaintVariants(
      this.#variants,
      variant,
      activeApplication
    );

    if (
      !optimizableVariants.length &&
      variant.available !== false &&
      variantMatchesApplication(variant, activeApplication)
    ) {
      optimizableVariants = [variant];
    }

    const fallbackVariant =
      [...optimizableVariants].sort(
        (a, b) => (resolveVariantLiters(b) ?? 0) - (resolveVariantLiters(a) ?? 0)
      )[0] ?? dataVariant;

    const calculationMode = /** @type {'auto' | 'per_liter' | 'per_can'} */ (
      this.dataset.calculationMode || 'auto'
    );
    const rangeStrategy = /** @type {'min' | 'max' | 'avg'} */ (this.dataset.rangeStrategy || 'min');
    const reservePercentage = this.#getReservePercentage();
    const mode = resolveCalculationMode(dataVariant, calculationMode, rangeStrategy);

    if (!mode) {
      this.#hideResults();
      return;
    }

    const consumption = resolveConsumption(dataVariant, rangeStrategy);
    const litersPerCan = resolveVariantLiters(fallbackVariant);

    const result = calculatePaint({
      walls: this.#walls.getWallsForCalculation(),
      openings: this.#openings?.getOpeningsForCalculation() ?? null,
      layers: this.#getSelectedLayers(),
      mode,
      m2PerLiter: consumption?.m2PerLiter ?? null,
      litersPerCan,
      m2PerCan: consumption?.m2PerCan ?? dataVariant.m2_per_can,
      reservePercentage,
      perLayer: consumption?.perLayer !== false,
      compatibleVariants: optimizableVariants,
      fallbackVariantId: fallbackVariant.id,
    });

    if (!result) {
      this.#hideResults();
      return;
    }

    this.#showResults(result, dataVariant);
    this.#updateSectionBadges();
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult} result
   * @param {object} variant
   */
  #showResults(result, variant) {
    const selectionPanel = this.querySelector('[data-hds-calculator-selection-panel]');
    const resultValue = this.querySelector('[data-hds-calculator-result-value]');
    const selectionTotal = this.querySelector('[data-hds-calculator-selection-total]');
    const explanation = this.querySelector('[data-hds-calculator-explanation]');
    const addToCartBtn = this.querySelector('[data-hds-calculator-add-to-cart]');

    this.#lastCombination = result.combination ?? null;
    this.#lastResult = result;

    if (resultValue) {
      resultValue.innerHTML = this.#renderCartSelectionHtml(result);
    }
    this.#updateSelectionImage(result);
    if (selectionTotal) {
      selectionTotal.textContent = this.#formatSelectionTotal(result);
    }
    selectionPanel?.classList.remove('hidden');

    if (explanation) explanation.innerHTML = this.#generateExplanation(result, variant);
    if (addToCartBtn instanceof HTMLButtonElement) {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent =
        addToCartBtn.dataset.hdsCalculatorAddToCartLabel || addToCartBtn.textContent;
    }
  }

  #hideResults() {
    const selectionPanel = this.querySelector('[data-hds-calculator-selection-panel]');
    const resultValue = this.querySelector('[data-hds-calculator-result-value]');
    const selectionTotal = this.querySelector('[data-hds-calculator-selection-total]');
    const explanation = this.querySelector('[data-hds-calculator-explanation]');
    const addToCartBtn = this.querySelector('[data-hds-calculator-add-to-cart]');

    selectionPanel?.classList.add('hidden');
    if (resultValue) resultValue.innerHTML = '';
    this.#updateSelectionImage(null);
    if (selectionTotal) selectionTotal.textContent = '—';
    if (explanation) explanation.innerHTML = '';
    this.#lastCombination = null;
    this.#lastResult = null;
    if (addToCartBtn instanceof HTMLButtonElement) {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = addToCartBtn.dataset.hdsCalculatorAddToCartLabel || addToCartBtn.textContent;
    }
    this.#updateSectionBadges();
  }

  /**
   * @param {HTMLElement} container
   */
  #getOpeningsLabels(container) {
    return {
      opening: container.dataset.openingsLabelOpening || 'Deur/raam',
      width: container.dataset.openingsLabelWidth || 'Breedte (cm)',
      height: container.dataset.openingsLabelHeight || 'Hoogte (cm)',
      remove: container.dataset.openingsLabelRemove || 'Verwijderen',
    };
  }

  /**
   * @returns {object}
   */
  #getExplanationLabels() {
    const container = this.querySelector('[data-hds-calculator-openings]');
    const uiLabels = container instanceof HTMLElement ? this.#getOpeningsLabels(container) : {};

    return {
      grossStepTitle: this.dataset.explanationSurfaceTitle || 'Oppervlak',
      openingsStepTitle: this.dataset.explanationOpeningsTitle || 'Uitsparingen',
      openingsIntro: 'Deuren en ramen schilder je niet mee. Dat oppervlak halen we van het totaal af.',
      netAreaLabel: 'Wandoppervlak om te schilderen',
      excludedLabel: 'niet meeschilderen',
      doorLabel: uiLabels.opening || 'Deur/raam',
      windowLabel: uiLabels.opening || 'Deur/raam',
    };
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult} result
   * @param {object} variant
   */
  #generateExplanation(result, variant) {
    const consumption = resolveConsumption(variant, this.dataset.rangeStrategy || 'min');

    return buildPaintExplanationHtml({
      result,
      walls: this.#walls?.getWallsForCalculation() ?? [],
      openings: this.#openings?.getOpeningsForCalculation() ?? null,
      consumption,
      variant,
      perLayer: consumption?.perLayer !== false,
      labels: this.#getExplanationLabels(),
    });
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult} result
   * @returns {string}
   */
  #renderCartSelectionHtml(result) {
    const pricing = this.#getCartSelectionPricing(result);
    if (!pricing?.lines.length) {
      if (result.quantity > 0) {
        return `
          <div class="hds-calculator__selection-line">
            <span class="hds-calculator__selection-line-qty">${result.quantity}×</span>
            <span class="hds-calculator__selection-line-label">blik</span>
            <span class="hds-calculator__selection-line-price" aria-hidden="true"></span>
          </div>
        `;
      }
      return '';
    }

    return pricing.lines
      .map((line) => {
        const lineTotal = this.#formatMoney(line.lineTotal);
        return `
          <div class="hds-calculator__selection-line">
            <span class="hds-calculator__selection-line-qty">${line.quantity}×</span>
            <span class="hds-calculator__selection-line-label">${line.label}</span>
            <span class="hds-calculator__selection-line-price">${lineTotal}</span>
          </div>
        `;
      })
      .join('');
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult | null} result
   */
  #updateSelectionImage(result) {
    const imageWrapper = this.querySelector('[data-hds-calculator-selection-image]');
    const img = imageWrapper?.querySelector('img');
    if (!(imageWrapper instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;

    const variantId = this.#getLargestCombinationVariantId(result?.combination);
    const variant = variantId ? this.#getVariantData(String(variantId)) : null;
    const imageUrl = variant?.image_url;

    if (imageUrl) {
      img.src = imageUrl;
      img.alt = variant?.title || '';
      imageWrapper.classList.remove('hidden');
      return;
    }

    img.removeAttribute('src');
    img.alt = '';
    imageWrapper.classList.add('hidden');
  }

  /**
   * @param {import('@theme/paint-calculator-core').OptimizedCombination | null | undefined} combination
   * @returns {string | number | null}
   */
  #getLargestCombinationVariantId(combination) {
    if (!combination?.items?.length) return null;

    const largestItem = [...combination.items].sort((a, b) => (b.units ?? 0) - (a.units ?? 0))[0];
    return largestItem?.id ?? null;
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult} result
   * @returns {string}
   */
  #formatSelectionTotal(result) {
    const pricing = this.#getCartSelectionPricing(result);
    if (!pricing) return '—';
    return this.#formatMoney(pricing.totalCents);
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult} result
   */
  #getCartSelectionPricing(result) {
    return buildCartSelectionPricing(result.combination, (variantId) => {
      const variant = this.#getVariantData(String(variantId));
      if (!variant) return null;
      return {
        label:
          result.mode === 'per_can' && variant.m2_per_can
            ? `${variant.m2_per_can} m²/blik`
            : formatVolumeDisplay(variant.volume, variant.volume_unit),
        unitPrice: parseInt(String(variant.price ?? 0), 10) || 0,
      };
    });
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    return formatMoneyFromCents(cents, this.dataset.moneyFormat || '{{amount}}').replace(',00', ',-');
  }

  /**
   * @param {import('@theme/paint-calculator-core').PaintCalculationResult} result
   * @returns {string}
   */
  #formatCartSelection(result) {
    const selection = formatCartSelectionDisplay(result.combination, (variantId) => {
      const variant = this.#getVariantData(String(variantId));
      if (!variant) return null;
      if (result.mode === 'per_can' && variant.m2_per_can) {
        return `${variant.m2_per_can} m²/blik`;
      }
      return formatVolumeDisplay(variant.volume, variant.volume_unit);
    });

    if (selection) return selection;
    if (result.quantity > 0) return `${result.quantity}× blik`;
    return '';
  }

  /**
   * @returns {Element | null}
   */
  #productFormScope() {
    return (
      this.closest('.product-information')?.querySelector('product-form-component') ??
      this.closest('.product-details')?.querySelector('product-form-component') ??
      this.closest('main')?.querySelector('product-form-component') ??
      null
    );
  }

  /**
   * @returns {string | null}
   */
  #getActiveApplication() {
    const paintFilter = document.querySelector('paint-application-filter');
    const checked = paintFilter?.querySelector('input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement && checked.value) {
      return checked.value.trim();
    }
    return null;
  }

  /**
   * @returns {string}
   */
  #getCurrentVariantId() {
    const productForm = this.#productFormScope();
    const variantInput =
      productForm?.querySelector('[ref="variantId"]') ??
      productForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      return variantInput.value;
    }

    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return fromUrl;

    return this.dataset.initialVariantId || '';
  }

  /**
   * @param {unknown} id
   * @returns {string | null}
   */
  #normalizeVariantId(id) {
    const normalized = String(id ?? '').trim();
    return /^\d+$/.test(normalized) ? normalized : null;
  }

  /**
   * @param {number | string} id
   * @returns {number}
   */
  #parseVariantIdForCart(id) {
    const numericId = typeof id === 'string' ? parseInt(id, 10) : Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      throw new Error(`Ongeldig variant ID: ${id}`);
    }
    return numericId;
  }

  /**
   * @returns {Record<string, string>}
   */
  #getLineItemProperties() {
    const productForm = this.#productFormScope()?.querySelector('form');
    if (!(productForm instanceof HTMLFormElement)) return {};

    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of new FormData(productForm).entries()) {
      if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
      properties[key.slice(11, -1)] = String(value);
    }
    return properties;
  }

  /**
   * @returns {Array<{ id: string, quantity: number }>}
   */
  #getCartItemsForAdd() {
    if (this.#lastCombination?.items?.length) {
      return this.#lastCombination.items
        .map((item) => {
          const id = this.#normalizeVariantId(item.id);
          const quantity = parseInt(String(item.quantity), 10);
          if (!id || !Number.isFinite(quantity) || quantity <= 0) return null;
          return { id, quantity };
        })
        .filter((item) => item != null);
    }

    const variantId = this.#normalizeVariantId(this.#getCurrentVariantId());
    if (!variantId) return [];

    return [{ id: variantId, quantity: 1 }];
  }

  /**
   * @param {Array<{ id: string, quantity: number }>} cartItems
   * @param {boolean} [useStringIds]
   * @returns {Promise<object>}
   */
  async #requestCartAdd(cartItems, useStringIds = false) {
    const properties = this.#getLineItemProperties();
    const sectionIds = getBundledCartSectionIds();

    const items = cartItems.map((item) => {
      const lineItem = {
        id: useStringIds ? item.id : this.#parseVariantIdForCart(item.id),
        quantity: item.quantity,
      };
      if (Object.keys(properties).length) {
        lineItem.properties = properties;
      }
      return lineItem;
    });

    /** @type {Record<string, unknown>} */
    const body = { items };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const responseText = await response.text();
    let data;
    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error('Ongeldige reactie van winkelmand');
    }

    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Failed to add to cart';
      /** @type {Error & { responseStatus?: number }} */
      const error = new Error(message);
      error.responseStatus = response.status;
      throw error;
    }

    return data;
  }

  /**
   * @param {Array<{ id: string, quantity: number }>} cartItems
   * @returns {Promise<object>}
   */
  async #postCartAdd(cartItems) {
    if (!cartItems.length) {
      throw new Error('Geen geldige varianten om toe te voegen');
    }

    try {
      return await this.#requestCartAdd(cartItems);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const responseStatus =
        error instanceof Error && 'responseStatus' in error
          ? /** @type {number | undefined} */ (error.responseStatus)
          : undefined;

      if (
        responseStatus === 422 &&
        (message.includes('variant niet vinden') || message.includes('variant not found'))
      ) {
        return this.#requestCartAdd(cartItems, true);
      }

      throw error;
    }
  }

  async #addToCart() {
    const addToCartBtn = this.querySelector('[data-hds-calculator-add-to-cart]');
    if (!(addToCartBtn instanceof HTMLButtonElement)) return;

    const cartItems = this.#getCartItemsForAdd();
    if (!cartItems.length) return;

    const originalText =
      addToCartBtn.dataset.hdsCalculatorAddToCartLabel || 'In winkelwagen';
    addToCartBtn.disabled = true;
    addToCartBtn.textContent = 'Toevoegen...';

    try {
      const data = await this.#postCartAdd(cartItems);

      document.dispatchEvent(
        new CartAddEvent(data, addToCartBtn.id || 'paint-calculator', {
          source: 'paint-calculator',
          itemCount: data.item_count,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      this.#closeModal();
      this.#showSuccessMessage(this.#formatCartSuccessMessage(cartItems, data));
    } catch (error) {
      console.error('Paint calculator add to cart error:', error);
      addToCartBtn.textContent = 'Fout - Probeer opnieuw';
      setTimeout(() => {
        addToCartBtn.textContent = originalText;
        addToCartBtn.disabled = false;
      }, 2000);
    }
  }

  /**
   * @param {Array<{ id: number | string, quantity: number }>} cartItems
   * @param {object} [cartData]
   * @returns {string}
   */
  #formatCartSuccessMessage(cartItems, cartData) {
    const combination = {
      items: cartItems.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        label: null,
        units: 0,
        price: 0,
      })),
      totalQuantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      totalUnits: 0,
      totalCost: 0,
    };

    const pricing = buildCartSelectionPricing(combination, (variantId) => {
      const variant = this.#getVariantData(String(variantId));
      return variant
        ? {
            label: formatVolumeDisplay(variant.volume, variant.volume_unit),
            unitPrice: parseInt(String(variant.price ?? 0), 10) || 0,
          }
        : null;
    });

    const selection = formatCartSelectionDisplay(combination, (variantId) => {
      const variant = this.#getVariantData(String(variantId));
      return variant ? formatVolumeDisplay(variant.volume, variant.volume_unit) : null;
    });

    const total = pricing ? this.#formatMoney(pricing.totalCents) : null;
    if (selection && total) return `${selection} (${total}) toegevoegd aan winkelmand`;
    if (selection) return `${selection} toegevoegd aan winkelmand`;

    const totalCans = combination.totalQuantity;
    return `${totalCans} blik${totalCans === 1 ? '' : 'ken'} toegevoegd aan winkelmand`;
  }

  /**
   * @param {string} message
   */
  #showSuccessMessage(message) {
    const notification = document.createElement('div');
    notification.className = 'hds-calculator__notification';
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  }
}

if (!customElements.get('paint-calculator')) {
  customElements.define('paint-calculator', PaintCalculator);
}
